import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Loader2, ShieldCheck } from 'lucide-react'
import { ContractorLogin } from 'modules/auth/components/ContractorLogin'
import { isAzureConfigured, startAzureLogin } from 'modules/auth/api/azureAuth'
import { Button } from 'shared/ui/button'
import { Alert, AlertDescription } from 'shared/ui/alert'

type ContractorLoginPageMode = 'checking' | 'local' | 'error'

const resolveErrorMessage = (error: any) => {
  const data = error?.response?.data
  return (
    (typeof data === 'string' ? data : null) ??
    (typeof data?.message === 'string' ? data.message : null) ??
    error?.message ??
    'Unable to redirect to Azure sign-in right now.'
  )
}

export function ContractorLoginPage() {
  const navigate = useNavigate()
  const [mode, setMode] = useState<ContractorLoginPageMode>('checking')
  const [error, setError] = useState('')
  const [retryToken, setRetryToken] = useState(0)

  useEffect(() => {
    let active = true

    const syncContractorLoginRoute = async () => {
      setMode('checking')
      setError('')

      try {
        const isContractorAzureEnabled = await isAzureConfigured('CONTRACTOR')
        if (!active) {
          return
        }

        if (!isContractorAzureEnabled) {
          setMode('local')
          return
        }

        await startAzureLogin('CONTRACTOR')
      } catch (loginError: any) {
        if (!active) {
          return
        }

        console.error('Failed to redirect contractor route to Azure', loginError)
        setError(resolveErrorMessage(loginError))
        setMode('error')
      }
    }

    void syncContractorLoginRoute()

    return () => {
      active = false
    }
  }, [retryToken])

  const handleLogin = (userData: any) => {
    navigate('/modules', { replace: true, state: { user: userData } })
  }

  const handleBack = () => {
    navigate('/login')
  }

  if (mode === 'local') {
    return <ContractorLogin onLogin={handleLogin} onBack={handleBack} />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 flex items-center justify-center p-6">
      <div className="w-full max-w-xl mx-auto flex flex-col items-center gap-4">
        <Button
          type="button"
          variant="ghost"
          onClick={handleBack}
          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 self-start"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          กลับไปหน้าเลือกประเภท
        </Button>

        <div className="w-full rounded-3xl border border-orange-100 bg-white/95 p-8 shadow-xl backdrop-blur">
          <div className="flex flex-col items-center gap-4 text-center">
            {mode === 'checking' ? (
              <>
                <div className="rounded-full bg-orange-100 p-4 text-orange-600">
                  <Loader2 className="h-10 w-10 animate-spin" />
                </div>
                <div className="space-y-2">
                  <h1 className="text-2xl font-semibold text-orange-950">Checking contractor sign-in</h1>
                  <p className="text-sm text-orange-900/70">
                    We are checking whether Contractor Azure AD login is enabled before opening the sign-in flow.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-full bg-red-50 p-4 text-red-500">
                  <AlertTriangle className="h-10 w-10" />
                </div>
                <div className="space-y-2">
                  <h1 className="text-2xl font-semibold text-slate-900">Unable to continue with Azure sign-in</h1>
                  <p className="text-sm text-slate-600">
                    The contractor route is configured to use Azure AD, but the redirect could not be started.
                  </p>
                </div>
                <Alert variant="destructive" className="w-full text-left">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Button
                    type="button"
                    className="bg-orange-500 hover:bg-orange-600"
                    onClick={() => setRetryToken((value) => value + 1)}
                  >
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Try Azure Again
                  </Button>
                  <Button type="button" variant="outline" onClick={handleBack}>
                    Back to Login Selection
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
